import { useCallback, useEffect, useRef, useState } from 'react';
import { getDefaultStore } from 'jotai';
import { authService, type AdminSessionTimingResponse } from '@/services/authService';
import { authUserMetaAtom } from '@/store/authAtoms';

const DEFAULT_IDLE_MINUTES = 30;
const DEFAULT_ABSOLUTE_HOURS = 8;
const WARNING_SECONDS = 60;
const WARNING_LEAD_MS = WARNING_SECONDS * 1000;
const ACTIVITY_SYNC_INTERVAL_MS = 30_000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'keydown',
  'mousedown',
  'touchstart',
  'scroll',
];

const store = getDefaultStore();

function deadline(raw: string | undefined, fallbackMs: number): number {
  const parsed = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : Date.now() + fallbackMs;
}

function warningDelay(deadlineMs: number): number {
  return Math.max(0, deadlineMs - Date.now() - WARNING_LEAD_MS);
}

function secondsRemaining(deadlineMs: number): number {
  return Math.max(1, Math.min(WARNING_SECONDS, Math.ceil((deadlineMs - Date.now()) / 1000)));
}

export type AdminScreenSessionPhase = 'active' | 'idle-warning' | 'absolute-warning';

export interface AdminScreenSessionState {
  phase: AdminScreenSessionPhase;
  countdown: number;
  extend: () => void;
  logout: () => void;
}

export function useAdminScreenSession(): AdminScreenSessionState {
  const [phase, setPhase] = useState<AdminScreenSessionPhase>('active');
  const [countdown, setCountdown] = useState(0);

  const phaseRef = useRef<AdminScreenSessionPhase>('active');
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const absoluteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleMsRef = useRef(DEFAULT_IDLE_MINUTES * 60_000);
  const lastActivitySyncRef = useRef(0);
  const syncInFlightRef = useRef(false);
  const extendingRef = useRef(false);
  const loggingOutRef = useRef(false);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = null;
  }, []);

  const clearIdle = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  }, []);

  const clearAbsolute = useCallback(() => {
    if (absoluteTimerRef.current) clearTimeout(absoluteTimerRef.current);
    absoluteTimerRef.current = null;
  }, []);

  const logout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    clearCountdown();
    clearIdle();
    clearAbsolute();
    void authService.logout()
      .catch(() => undefined)
      .finally(() => store.set(authUserMetaAtom, null));
  }, [clearAbsolute, clearCountdown, clearIdle]);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const startCountdown = useCallback((
    nextPhase: Exclude<AdminScreenSessionPhase, 'active'>,
    deadlineMs: number,
  ) => {
    clearCountdown();
    phaseRef.current = nextPhase;
    setPhase(nextPhase);

    const tick = () => {
      if (deadlineMs <= Date.now()) {
        clearCountdown();
        setCountdown(0);
        logoutRef.current();
        return;
      }
      setCountdown(secondsRemaining(deadlineMs));
    };

    tick();
    countdownRef.current = setInterval(tick, 1000);
  }, [clearCountdown]);

  const scheduleIdle = useCallback((deadlineMs: number) => {
    clearIdle();
    idleTimerRef.current = setTimeout(() => {
      if (phaseRef.current === 'active') startCountdown('idle-warning', deadlineMs);
    }, warningDelay(deadlineMs));
  }, [clearIdle, startCountdown]);

  const scheduleAbsolute = useCallback((deadlineMs: number) => {
    clearAbsolute();
    absoluteTimerRef.current = setTimeout(() => {
      clearIdle();
      startCountdown('absolute-warning', deadlineMs);
    }, warningDelay(deadlineMs));
  }, [clearAbsolute, clearIdle, startCountdown]);

  const applyTiming = useCallback((timing: AdminSessionTimingResponse) => {
    const idleMinutes = Number(timing.idleTimeoutMinutes);
    const idleMs = Number.isFinite(idleMinutes) && idleMinutes > 0
      ? idleMinutes * 60_000
      : DEFAULT_IDLE_MINUTES * 60_000;
    idleMsRef.current = idleMs;
    scheduleIdle(deadline(timing.idleExpiresAt, idleMs));
    scheduleAbsolute(deadline(
      timing.absoluteExpiresAt,
      DEFAULT_ABSOLUTE_HOURS * 3_600_000,
    ));
  }, [scheduleAbsolute, scheduleIdle]);

  const syncActivity = useCallback(async () => {
    if (syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    lastActivitySyncRef.current = Date.now();
    try {
      applyTiming(await authService.recordActivity());
    } finally {
      syncInFlightRef.current = false;
    }
  }, [applyTiming]);

  const extend = useCallback(async () => {
    if (phaseRef.current !== 'idle-warning' || extendingRef.current) return;
    extendingRef.current = true;
    clearCountdown();
    try {
      const timing = await authService.recordActivity();
      phaseRef.current = 'active';
      setPhase('active');
      setCountdown(0);
      lastActivitySyncRef.current = Date.now();
      applyTiming(timing);
    } catch {
      logoutRef.current();
    } finally {
      extendingRef.current = false;
    }
  }, [applyTiming, clearCountdown]);

  const resetIdle = useCallback(() => {
    if (phaseRef.current !== 'active') return;
    scheduleIdle(Date.now() + idleMsRef.current);
    if (Date.now() - lastActivitySyncRef.current >= ACTIVITY_SYNC_INTERVAL_MS) {
      void syncActivity().catch(() => undefined);
    }
  }, [scheduleIdle, syncActivity]);

  useEffect(() => {
    void syncActivity().catch(() => logoutRef.current());
    ACTIVITY_EVENTS.forEach(eventName => {
      document.addEventListener(eventName, resetIdle, { passive: true });
    });
    return () => {
      clearIdle();
      clearAbsolute();
      clearCountdown();
      ACTIVITY_EVENTS.forEach(eventName => document.removeEventListener(eventName, resetIdle));
    };
  }, [clearAbsolute, clearCountdown, clearIdle, resetIdle, syncActivity]);

  return { phase, countdown, extend, logout };
}
